import camelCase from 'lodash/camelCase'

/**
 * Recursively converts all object keys to camelCase, including nested objects and arrays.
 *
 * This function handles:
 * - Primitives, null, and Date objects (returned as-is)
 * - Plain objects, including nested, which are converted to camelCase keys
 * - Arrays (recursively applies conversion to all elements)
 *
 * Limitations:
 * - Does not convert keys in Map, Set, or custom class instances
 * - May not preserve prototype chain or non-enumerable properties
 * - Intended for JSON-like data structures
 */
export function convertObjectKeysToCamelCase<T>(input: T): T {
  if (input === null || typeof input !== 'object' || input instanceof Date) {
    return input // For null and primitive values, return as is
  }

  if (Array.isArray(input)) {
    return input.map(convertObjectKeysToCamelCase) as T
  }

  const result: Record<string, any> = {}

  for (const [key, value] of Object.entries(input)) {
    const convertedKey = camelCase(key)

    if (value === null || typeof value !== 'object' || value instanceof Date) {
      result[convertedKey] = value
    } else if (Array.isArray(value)) {
      result[convertedKey] = value.map(convertObjectKeysToCamelCase)
    } else if (typeof value === 'object') {
      result[convertedKey] = convertObjectKeysToCamelCase(value)
    }
  }
  return result as T
}
